"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, Copy, Eye, ImageIcon, Sparkles, Wrench } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useI18n } from "@llmgateway/i18n/react";

/**
 * Model data structure from list-models tool
 */
interface ModelData {
  id: string;
  name: string;
  family?: string;
  providers: string[];
  capabilities: {
    vision: boolean;
    tools: boolean;
    reasoning: boolean;
    streaming: boolean;
    imageGeneration: boolean;
  };
  pricing: {
    input: string;
    output: string;
  };
  context_length?: number;
  free: boolean;
}

/**
 * Image model data structure from list-image-models tool
 */
interface ImageModelData {
  id: string;
  name: string;
  description?: string;
  family?: string;
  requestPrice?: number;
  providers: string[];
}

/**
 * Type guard to check if output contains models list
 */
export function hasModelsOutput(output: unknown): output is {
  text: string;
  models: ModelData[];
} {
  if (typeof output !== "object" || output === null) {
    return false;
  }
  if (!("models" in output) || !Array.isArray((output as any).models)) {
    return false;
  }
  const models = (output as any).models;
  if (models.length === 0) {
    return false;
  }
  const first = models[0];
  return typeof first === "object" && first !== null && "id" in first && "capabilities" in first;
}

/**
 * Type guard to check if output contains image models list
 */
export function hasImageModelsOutput(output: unknown): output is {
  text: string;
  imageModels: ImageModelData[];
} {
  if (typeof output !== "object" || output === null) {
    return false;
  }
  if (!("imageModels" in output) || !Array.isArray((output as any).imageModels)) {
    return false;
  }
  const models = (output as any).imageModels;
  if (models.length === 0) {
    return false;
  }
  const first = models[0];
  return typeof first === "object" && first !== null && "id" in first;
}

/**
 * Model card component for displaying LLM models
 */
function ModelCard(t0) {
  const $ = _c(109);
  const {
    model
  } = t0;
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== model.id) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(model.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = model.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyToClipboard = t1;
  let capabilities;
  if ($[2] !== model.capabilities.imageGeneration || $[3] !== model.capabilities.reasoning || $[4] !== model.capabilities.tools || $[5] !== model.capabilities.vision || $[6] !== t) {
    capabilities = [];
    if (model.capabilities.vision) {
      let t2;
      if ($[8] !== t) {
        t2 = t("modelSelector.capabilityVision");
        $[8] = t;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      let t3;
      if ($[10] !== t2) {
        t3 = {
          key: "vision",
          label: t2
        };
        $[10] = t2;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      capabilities.push(t3);
    }
    if (model.capabilities.tools) {
      let t2;
      if ($[12] !== t) {
        t2 = t("modelSelector.capabilityTools");
        $[12] = t;
        $[13] = t2;
      } else {
        t2 = $[13];
      }
      let t3;
      if ($[14] !== t2) {
        t3 = {
          key: "tools",
          label: t2
        };
        $[14] = t2;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      capabilities.push(t3);
    }
    if (model.capabilities.reasoning) {
      let t2;
      if ($[16] !== t) {
        t2 = t("modelSelector.capabilityReasoning");
        $[16] = t;
        $[17] = t2;
      } else {
        t2 = $[17];
      }
      let t3;
      if ($[18] !== t2) {
        t3 = {
          key: "reasoning",
          label: t2
        };
        $[18] = t2;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      capabilities.push(t3);
    }
    if (model.capabilities.imageGeneration) {
      let t2;
      if ($[20] !== t) {
        t2 = t("modelSelector.capabilityImageGeneration");
        $[20] = t;
        $[21] = t2;
      } else {
        t2 = $[21];
      }
      let t3;
      if ($[22] !== t2) {
        t3 = {
          key: "imageGeneration",
          label: t2
        };
        $[22] = t2;
        $[23] = t3;
      } else {
        t3 = $[23];
      }
      capabilities.push(t3);
    }
    $[2] = model.capabilities.imageGeneration;
    $[3] = model.capabilities.reasoning;
    $[4] = model.capabilities.tools;
    $[5] = model.capabilities.vision;
    $[6] = t;
    $[7] = capabilities;
  } else {
    capabilities = $[7];
  }
  const formatContextSize = _temp;
  let t2;
  if ($[24] !== model.name) {
    t2 = <h4 className="font-semibold text-sm truncate">{model.name}</h4>;
    $[24] = model.name;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  let t3;
  if ($[26] !== model.family) {
    t3 = model.family && <Badge variant="secondary" className="mt-1 text-[10px]">{model.family}</Badge>;
    $[26] = model.family;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[28] !== t2 || $[29] !== t3) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] !== model.free || $[32] !== t) {
    t5 = model.free && <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px]">{t("aiElements.free")}</Badge>;
    $[31] = model.free;
    $[32] = t;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  let t6;
  if ($[34] !== t4 || $[35] !== t5) {
    t6 = <div className="flex items-start justify-between gap-2">{t4}{t5}</div>;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== model.id) {
    t7 = <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code>;
    $[37] = model.id;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t) {
    t8 = t("aiElements.copyModelId");
    $[39] = t;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== copied) {
    t9 = copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />;
    $[41] = copied;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== copyToClipboard || $[44] !== t8 || $[45] !== t9) {
    t10 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={copyToClipboard} title={t8}>{t9}</Button>;
    $[43] = copyToClipboard;
    $[44] = t8;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t7) {
    t11 = <div className="flex items-center gap-2">{t7}{t10}</div>;
    $[47] = t10;
    $[48] = t7;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t) {
    t12 = t("aiElements.context");
    $[50] = t;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== t12) {
    t13 = <span className="text-muted-foreground">{t12}:</span>;
    $[52] = t12;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== model.context_length) {
    t14 = formatContextSize(model.context_length) ?? "\u2014";
    $[54] = model.context_length;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== t14) {
    t15 = <span className="ml-1 font-medium">{t14}</span>;
    $[56] = t14;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== t13 || $[59] !== t15) {
    t16 = <div>{t13}{t15}</div>;
    $[58] = t13;
    $[59] = t15;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t) {
    t17 = t("aiElements.providers");
    $[61] = t;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t17) {
    t18 = <span className="text-muted-foreground">{t17}:</span>;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== model.providers.length) {
    t19 = <span className="ml-1 font-medium">{model.providers.length}</span>;
    $[65] = model.providers.length;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t18 || $[68] !== t19) {
    t20 = <div>{t18}{t19}</div>;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t16 || $[71] !== t20) {
    t21 = <div className="grid grid-cols-2 gap-2 text-xs">{t16}{t20}</div>;
    $[70] = t16;
    $[71] = t20;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== t) {
    t22 = t("aiElements.pricing");
    $[73] = t;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== t22) {
    t23 = <div className="text-muted-foreground">{t22}:</div>;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  let t24;
  if ($[77] !== model.pricing.input) {
    t24 = <span className="font-mono font-medium">{model.pricing.input}</span>;
    $[77] = model.pricing.input;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t) {
    t25 = t("aiElements.inputShort");
    $[79] = t;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== t25) {
    t26 = <span className="text-muted-foreground">{t25}</span>;
    $[81] = t25;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== t24 || $[84] !== t26) {
    t27 = <span>{t24}{" "}{t26}</span>;
    $[83] = t24;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== model.pricing.output) {
    t28 = <span className="font-mono font-medium">{model.pricing.output}</span>;
    $[86] = model.pricing.output;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== t) {
    t29 = t("aiElements.outputShort");
    $[88] = t;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== t29) {
    t30 = <span className="text-muted-foreground">{t29}</span>;
    $[90] = t29;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  let t31;
  if ($[92] !== t28 || $[93] !== t30) {
    t31 = <span>{t28}{" "}{t30}</span>;
    $[92] = t28;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  let t32;
  if ($[95] !== t27 || $[96] !== t31) {
    t32 = <div className="flex gap-3">{t27}{t31}</div>;
    $[95] = t27;
    $[96] = t31;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  let t33;
  if ($[98] !== t23 || $[99] !== t32) {
    t33 = <div className="text-xs space-y-1">{t23}{t32}</div>;
    $[98] = t23;
    $[99] = t32;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  let t34;
  if ($[101] !== capabilities) {
    t34 = capabilities.length > 0 && <div className="flex flex-wrap gap-1">{capabilities.map(_temp2)}</div>;
    $[101] = capabilities;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== t11 || $[104] !== t21 || $[105] !== t33 || $[106] !== t34 || $[107] !== t6) {
    t35 = <div className="flex-shrink-0 w-[280px] rounded-lg border bg-card p-4 space-y-3">{t6}{t11}{t21}{t33}{t34}</div>;
    $[103] = t11;
    $[104] = t21;
    $[105] = t33;
    $[106] = t34;
    $[107] = t6;
    $[108] = t35;
  } else {
    t35 = $[108];
  }
  return t35;
}

/**
 * Image model card component
 */
function _temp2(cap) {
  return <Badge key={cap.key} variant="outline" className="text-[10px] px-1.5 py-0">{cap.key === "vision" && <Eye className="h-2.5 w-2.5 mr-0.5" />}{cap.key === "tools" && <Wrench className="h-2.5 w-2.5 mr-0.5" />}{cap.key === "reasoning" && <Sparkles className="h-2.5 w-2.5 mr-0.5" />}{cap.key === "imageGeneration" && <ImageIcon className="h-2.5 w-2.5 mr-0.5" />}{cap.label}</Badge>;
}
function _temp(size) {
  if (!size) {
    return null;
  }
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}K`;
  }
  return size.toString();
}
function ImageModelCard(t0) {
  const $ = _c(35);
  const {
    model
  } = t0;
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== model.id) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(model.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = model.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyToClipboard = t1;
  let t2;
  if ($[2] !== model.name) {
    t2 = <h4 className="font-semibold text-sm truncate">{model.name}</h4>;
    $[2] = model.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== model.family) {
    t3 = model.family && <Badge variant="secondary" className="mt-1 text-[10px]">{model.family}</Badge>;
    $[4] = model.family;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ImageIcon className="h-4 w-4 text-muted-foreground" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <div className="flex items-start justify-between gap-2">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== model.description) {
    t7 = model.description && <p className="text-xs text-muted-foreground line-clamp-2">{model.description}</p>;
    $[12] = model.description;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== model.id) {
    t8 = <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code>;
    $[14] = model.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("aiElements.copyModelId");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== copied) {
    t10 = copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />;
    $[18] = copied;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== copyToClipboard || $[21] !== t10 || $[22] !== t9) {
    t11 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={copyToClipboard} title={t9}>{t10}</Button>;
    $[20] = copyToClipboard;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t8) {
    t12 = <div className="flex items-center gap-2">{t8}{t11}</div>;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== model.requestPrice || $[28] !== t) {
    t13 = model.requestPrice !== undefined && model.requestPrice > 0 && <div className="text-xs"><span className="text-muted-foreground">{t("aiElements.price")}:</span><span className="ml-1 font-mono font-medium">${model.requestPrice}</span><span className="text-muted-foreground">{" "}{t("aiElements.perRequest")}</span></div>;
    $[27] = model.requestPrice;
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t6 || $[33] !== t7) {
    t14 = <div className="flex-shrink-0 w-[260px] rounded-lg border bg-card p-4 space-y-3">{t6}{t7}{t12}{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t6;
    $[33] = t7;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}

/**
 * Models list output component with horizontal scroll
 */
export function ModelsListOutput(t0) {
  const $ = _c(15);
  const {
    models
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== models.length || $[1] !== t) {
    t1 = t("aiElements.modelsFound", {
      count: models.length
    });
    $[0] = models.length;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="flex items-center justify-between px-2"><span className="text-xs text-muted-foreground">{t1}</span></div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== models) {
    t3 = models.map(_temp3);
    $[5] = models;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="flex gap-3 p-2">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ScrollBar orientation="horizontal" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <ScrollArea className="w-full whitespace-nowrap">{t4}{t5}</ScrollArea>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <div className="space-y-2 py-2">{t2}{t6}</div>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

/**
 * Image models list output component with horizontal scroll
 */
function _temp3(model) {
  return <ModelCard key={model.id} model={model} />;
}
export function ImageModelsListOutput(t0) {
  const $ = _c(15);
  const {
    imageModels
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== imageModels.length || $[1] !== t) {
    t1 = t("aiElements.imageModelsFound", {
      count: imageModels.length
    });
    $[0] = imageModels.length;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="flex items-center justify-between px-2"><span className="text-xs text-muted-foreground">{t1}</span></div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== imageModels) {
    t3 = imageModels.map(_temp4);
    $[5] = imageModels;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="flex gap-3 p-2">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ScrollBar orientation="horizontal" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <ScrollArea className="w-full whitespace-nowrap">{t4}{t5}</ScrollArea>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <div className="space-y-2 py-2">{t2}{t6}</div>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

/**
 * Text content output component for generate_content results
 */
function _temp4(model) {
  return <ImageModelCard key={model.id} model={model} />;
}
export function TextContentOutput(t0) {
  const $ = _c(2);
  const {
    response
  } = t0;
  let t1;
  if ($[0] !== response) {
    t1 = <div className="p-3 rounded-md bg-muted/30 border"><div className="prose prose-sm dark:prose-invert max-w-none"><div className="whitespace-pre-wrap text-sm">{response}</div></div></div>;
    $[0] = response;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Type guard for generate_content output
 */
export function hasTextContentOutput(output: unknown): output is {
  response: string;
} {
  return typeof output === "object" && output !== null && "response" in output && typeof (output as any).response === "string";
}